// `mapbox-gl` is only present transitively through @adornis/maps, so we don't import
// its types directly. The runtime API surface we touch is the standard Mapbox GL JS Map.
type MapboxMap = any;

/**
 * Fetch a driving route polyline between two points from the Mapbox Directions API.
 * Returns a GeoJSON LineString geometry, or `null` on failure (network, bad key, no route).
 */
async function fetchDrivingRoute(
  token: string,
  from: { lat: number; lng: number },
  to: { lat: number; lng: number },
): Promise<GeoJSON.LineString | null> {
  const url =
    `https://api.mapbox.com/directions/v5/mapbox/driving/` +
    `${from.lng},${from.lat};${to.lng},${to.lat}` +
    `?geometries=geojson&overview=full&access_token=${encodeURIComponent(token)}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = (await res.json()) as {
      routes?: Array<{ geometry?: GeoJSON.LineString }>;
    };
    return data.routes?.[0]?.geometry ?? null;
  } catch {
    return null;
  }
}

/**
 * Draw (or replace) a route layer on a Mapbox map. Idempotent — safe to call
 * multiple times for the same `id`; the existing layer/source is removed first.
 */
function drawRouteLayer(
  map: MapboxMap,
  id: string,
  geometry: GeoJSON.LineString,
  color = '#3b82f6',
  lineWidth = 4,
): void {
  const sourceId = `${id}-source`;
  if (map.getLayer(id)) map.removeLayer(id);
  if (map.getSource(sourceId)) map.removeSource(sourceId);

  map.addSource(sourceId, {
    type: 'geojson',
    data: { type: 'Feature', properties: {}, geometry },
  });
  map.addLayer({
    id,
    type: 'line',
    source: sourceId,
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: { 'line-color': color, 'line-width': lineWidth, 'line-opacity': 0.85 },
  });
}

/**
 * Convenience: fetch a driving route and add it to the map once it has finished loading.
 * If the directions API fails, no layer is added — the markers/straight-line stays.
 */
export function addDrivingRouteWhenReady(
  map: MapboxMap,
  token: string,
  from: { lat: number; lng: number },
  to: { lat: number; lng: number },
  id = 'driving-route',
): void {
  const run = async () => {
    const geom = await fetchDrivingRoute(token, from, to);
    if (geom) drawRouteLayer(map, id, geom);
  };
  if (map.isStyleLoaded()) {
    void run();
  } else {
    map.once('load', () => void run());
  }
}
